import React, { useContext, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Cpu,
  Bot,
  Network,
  FileCheck,
  Coins,
  Brain,
  Store,
  Settings,
  Vote,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Bell,
  Sparkles,
  Languages
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NeuroMesh Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e21b96fc-2063-4121-8d2d-76327032f520/artifacts/31nh1s2f_NeuroMesh%20Logo%20%282%29.png";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/cerebro", label: "Cerebro", icon: Brain, highlight: true },
  { path: "/robomesh", label: "RoboMesh", icon: Sparkles, highlight: true },
  { path: "/superbrain", label: "Superbrain", icon: Network },
  { path: "/nodes", label: "Nodes", icon: Cpu },
  { path: "/proofs", label: "Proof Logs", icon: FileCheck },
  { path: "/credits", label: "Compute Credits", icon: Coins },
  { path: "/agents", label: "Sandbox", icon: Bot },
  { path: "/marketplace", label: "Marketplace", icon: Store },
  { path: "/governance", label: "Governance", icon: Vote, disabled: true },
  { path: "/settings", label: "Settings", icon: Settings }
];

const networks = ["Mainnet", "Testnet", "Local"];

const AppLayout = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [network, setNetwork] = useState("Testnet");

  const isDemo = new URLSearchParams(location.search).get("demo") === "true";

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    toast.success("Logged out successfully");
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const appendDemo = (path) => {
    return isDemo ? `${path}?demo=true` : path;
  };

  return (
    <div className="min-h-screen bg-[#030303] flex" data-testid="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] border-r border-white/10 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Logo Header - clickable to go home */}
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/")}>
            <img 
              src={LOGO_URL} 
              alt="NeuroMesh" 
              className="w-8 h-8 object-cover"
            />
            <span className="font-heading text-lg font-bold tracking-tight text-white">
              NEURO<span className="nm-gradient-text">MESH</span>
            </span>
          </div>
          <button 
            className="ml-auto lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={appendDemo(item.path)}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                    ${item.disabled 
                      ? "text-gray-600 cursor-not-allowed pointer-events-none" 
                      : item.highlight
                        ? isActive
                          ? "bg-gradient-to-r from-[#00D2FF]/20 to-[#3399FF]/20 text-[#00D2FF] border border-[#00D2FF]/30"
                          : "bg-gradient-to-r from-[#00D2FF]/10 to-[#3399FF]/10 text-[#00D2FF] border border-[#00D2FF]/20 hover:border-[#00D2FF]/40"
                        : isActive 
                          ? "bg-[#00D2FF]/10 text-[#00D2FF] border-l-2 border-[#00D2FF]" 
                          : "text-gray-400 hover:text-white hover:bg-white/5"
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{translate(item.label, language)}</span>
                  {item.disabled && (
                    <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 border-gray-700 text-gray-600">
                      {translate("SOON", language)}
                    </Badge>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 p-2 rounded-md bg-white/5">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#00D2FF]/20 text-[#00D2FF] text-xs">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || "User"}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role || "developer"}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10 flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <button 
            className="lg:hidden mr-4 text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Workspace selector placeholder */}
          <div className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-gray-500">Workspace:</span>
            <span className="text-white font-medium">Default</span>
          </div>

          <div className="flex-1" />

          {/* Network selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 gap-2 border-white/10 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white"
                data-testid="network-selector"
              >
                <span className={`w-2 h-2 rounded-full ${
                  network === "Mainnet" ? "bg-[#00FF94]" : network === "Testnet" ? "bg-yellow-400" : "bg-[#00D2FF]"
                }`} />
                {network}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
              <DropdownMenuItem 
                disabled
                className="text-gray-600 cursor-not-allowed"
              >
                <span className="w-2 h-2 rounded-full mr-2 bg-gray-600" />
                Mainnet (Coming Soon)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setNetwork("Testnet")}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5"
              >
                <span className="w-2 h-2 rounded-full mr-2 bg-yellow-400" />
                Testnet
              </DropdownMenuItem>
              <DropdownMenuItem 
                disabled
                className="text-gray-600 cursor-not-allowed"
              >
                <span className="w-2 h-2 rounded-full mr-2 bg-gray-600" />
                Local (Coming Soon)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Language selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 gap-2 border-white/10 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white ml-2"
              >
                {LANGUAGES.find(l => l.code === language)?.flag || '🌐'}
                <span className="hidden md:inline">{LANGUAGES.find(l => l.code === language)?.name || 'English'}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem 
                  key={lang.code} 
                  onClick={() => {
                    setLanguage(lang.code);
                    toast.success(`Language changed to ${lang.name}`);
                    window.location.reload();  // Reload to retranslate all content
                  }}
                  className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5"
                >
                  <span className="text-lg mr-2">{lang.flag}</span>
                  {lang.name}
                  {language === lang.code && <span className="ml-auto text-cyan-400">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="ml-2 text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="notifications-btn"
          >
            <Bell className="w-4 h-4" />
          </Button>

          {/* Profile dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="ml-2 gap-2 hover:bg-white/5"
                data-testid="profile-dropdown"
              >
                <Avatar className="w-7 h-7">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-[#00D2FF]/20 text-[#00D2FF] text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#0A0A0A] border-white/10">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={() => navigate(appendDemo("/settings"))}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {isDemo && (
            <Badge className="ml-4 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              DEMO MODE
            </Badge>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
